import React, { useState, useEffect } from "react";
import Leftsidebar from "../sidebar/leftsidebar";
import LeftResponsivesidebar from "../sidebar/left-responsive";
import Headmenu from "../sidebar/headmenu";
import { Search, ArrowLeft, Home, Users, Grid, MessageSquare } from "lucide-react";

const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || "").replace(/\/$/, "");
const OrderManagement = (props) => {
    const [activeTab, setActiveTab] = useState("Pending");
    // API related state
    const [apiOrders, setApiOrders] = useState([]);
    const [loadingApi, setLoadingApi] = useState(true);
    const [apiError, setApiError] = useState(null);

    // dynamic counts for tabs
    const [statusCounts, setStatusCounts] = useState({
        Pending: null,
        Processing: null,
        Shipped: null,
        "In Transit": null,
        "Out for Delivery": null,
        Delivered: null,
    });
    const [countsLoading, setCountsLoading] = useState(false);
    const [countsError, setCountsError] = useState(null);

    // --- shipment modal state ---
    const [shipModalOpen, setShipModalOpen] = useState(false);
    const [shippingOrder, setShippingOrder] = useState(null);
    const [estimateDeliveryDate, setEstimateDeliveryDate] = useState("");
    const [deliveryRemark, setDeliveryRemark] = useState("");
    const [shipLoading, setShipLoading] = useState(false);
    const [shipError, setShipError] = useState(null);

    // Mark-as-processing client state
    const [processingLoadingId, setProcessingLoadingId] = useState(null);
    const [processingError, setProcessingError] = useState(null);
    const [clientProcessingOrders, setClientProcessingOrders] = useState([]);
    // client-side moved shipped orders (no server)
    const [clientShippedOrders, setClientShippedOrders] = useState([]);
    // client-side moved in-transit / out-for-delivery / delivered orders (no server)
    const [clientInTransitOrders, setClientInTransitOrders] = useState([]);
    const [clientOutForDeliveryOrders, setClientOutForDeliveryOrders] = useState([]);
    const [clientDeliveredOrders, setClientDeliveredOrders] = useState([]);
    // move request state
    const [moveLoadingId, setMoveLoadingId] = useState(null);
    const [moveError, setMoveError] = useState(null);

    const defaultOrders = [];

    const orders = props.orders || [];

    // Fetch counts for each status (Pending = status 'new' includes 'confirmed' in response)
    useEffect(() => {
        let controller = new AbortController();
        const token =
            sessionStorage.getItem("accessToken") ||
            localStorage.getItem("accessToken") ||
            "";

        const statuses = [
            { key: "Pending", param: "new" },
            { key: "Processing", param: "processing" },
            { key: "Shipped", param: "shipped" },
            { key: "In Transit", param: "transit" },
            { key: "Out for Delivery", param: "out_of_delivery" },
            { key: "Delivered", param: "delivered" },
        ];

        setCountsLoading(true);
        setCountsError(null);

        (async () => {
            try {
                const requests = statuses.map(({ param }) =>
                    fetch(
                        `${API_BASE_URL}/seller/orders?status=${encodeURIComponent(
                            param
                        )}&page=1&limit=10`,
                        {
                            headers: {
                                "Content-Type": "application/json",
                                ...(token ? { token } : {}),
                            },
                            signal: controller.signal,
                        }
                    ).then((r) => r.ok ? r.json() : Promise.resolve({}))
                );

                const results = await Promise.all(requests);

                const newCounts = {};
                results.forEach((json, i) => {
                    const total =
                        json?.result?.pagination?.total ??
                        (Array.isArray(json?.result?.orders) ? json.result.orders.length : 0);
                    newCounts[statuses[i].key] = Number(total || 0);
                });

                setStatusCounts((prev) => ({ ...prev, ...newCounts }));
            } catch (err) {
                if (err.name !== "AbortError") {
                    console.error("Error fetching status counts:", err);
                    setCountsError(err.message || "Failed to load counts");
                }
            } finally {
                setCountsLoading(false);
            }
        })();

        return () => controller.abort();
    }, []); // run on mount

    // fetch actual orders for the active tab (API-backed tabs)
    useEffect(() => {
        let controller = new AbortController();

        const fetchByTab = async () => {
            const statusMap = {
                Pending: "new",
                Processing: "processing",
                Shipped: "shipped",
                "In Transit": "transit",
                "Out for Delivery": "out_of_delivery",
                Delivered: "delivered",
            };
            const statusParam = statusMap[activeTab];
            setLoadingApi(true);
            setApiError(null);

            try {
                const token =
                    sessionStorage.getItem("accessToken") ||
                    localStorage.getItem("accessToken") ||
                    "";

                const statusQuery = statusParam ? `status=${encodeURIComponent(statusParam)}&` : "";
                const url = `${API_BASE_URL}/seller/orders?${statusQuery}page=1&limit=10`;

                const res = await fetch(url, {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        ...(token ? { token } : {}),
                    },
                    signal: controller.signal,
                });

                if (!res.ok) throw new Error(`HTTP ${res.status}`);

                const json = await res.json();
                const apiList = json?.result?.orders || [];

                const mapStatus = (s) => {
                    if (!s) return "Pending";
                    if (s === "new" || s === "confirmed") return "Pending";
                    if (s === "processing") return "Processing";
                    if (s === "shipped") return "Shipped";
                    if (s === "transit") return "In Transit";
                    if (s === "out_of_delivery") return "Out for Delivery";
                    if (s === "delivered") return "Delivered";
                    // fallback: replace underscores and capitalize
                    return s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
                };

                const mapped = apiList.map((o) => {
                    const item = (o.items && o.items[0]) || {};
                    const product = item.product || {};
                    const mediaUrl = product?.product_media?.[0]?.url || null;
                    const shipping = o.shippingAddress || {};

                    return {
                        id: String(o.id || o.orderId || `${o.id}`),
                        orderId: o.orderId || String(o.id || ""),
                        date: o.placedAt ? new Date(o.placedAt).toLocaleDateString() : "",
                        deliveryDate: o.estimateDeliveryDate
                            ? new Date(o.estimateDeliveryDate).toLocaleDateString()
                            : "",
                        productName: item.productName || product.product_name || "Product",
                        color: "",
                        size: "",
                        price: Number(item.price ?? o.totalAmount ?? 0),
                        quantity: Number(item.quantity ?? 1),
                        discount: product?.offer_percentage ? `${product.offer_percentage}%` : (product?.discount_price ? `${product.discount_price}` : ""),
                        status: mapStatus(o.status),
                        paid: false,
                        image: mediaUrl || undefined,
                        shippingName: shipping.name,
                        shippingPhone: shipping.phone,
                        shippingEmail: shipping.email,
                        raw: o,
                    };
                });

                if (!controller.signal.aborted) setApiOrders(mapped);
            } catch (err) {
                if (!controller.signal.aborted && err.name !== "AbortError") {
                    console.error("Fetch orders error:", err);
                    setApiError(err.message || "Failed to load orders");
                }
            } finally {
                if (!controller.signal.aborted) setLoadingApi(false);
            }
        };

        fetchByTab();

        return () => {
            controller.abort();
        };
    }, [activeTab]);

    // Compose displayed orders (merge hard-coded and API results)
    const displayedOrders = (() => {
        if (activeTab === "All") {
            const map = new Map();
            [...orders, ...apiOrders].forEach((o) => map.set(o.id, o));
            return Array.from(map.values());
        }

        if (["Pending", "Processing", "Shipped", "In Transit", "Out for Delivery", "Delivered"].includes(activeTab)) {
            const defaultsForTab = orders.filter((o) => o.status === activeTab);
            const map = new Map();
            if (activeTab === "Processing") {
                // include client-side moved processing orders as well as API-backed ones
                [...defaultsForTab, ...clientProcessingOrders, ...apiOrders].forEach((o) => map.set(o.id, o));
            } else if (activeTab === "Shipped") {
                [...defaultsForTab, ...clientShippedOrders, ...apiOrders].forEach((o) => map.set(o.id, o));
            } else if (activeTab === "In Transit") {
                [...defaultsForTab, ...clientInTransitOrders, ...apiOrders].forEach((o) => map.set(o.id, o));
            } else if (activeTab === "Out for Delivery") {
                [...defaultsForTab, ...clientOutForDeliveryOrders, ...apiOrders].forEach((o) => map.set(o.id, o));
            } else if (activeTab === "Delivered") {
                [...defaultsForTab, ...clientDeliveredOrders, ...apiOrders].forEach((o) => map.set(o.id, o));
            } else {
                [...defaultsForTab, ...apiOrders].forEach((o) => map.set(o.id, o));
            }
            return Array.from(map.values());
        }

        return orders.filter((order) => order.status === activeTab);
    })();

    const renderCount = (key) =>
        statusCounts[key] === null ? "..." : ` (${statusCounts[key]})`;

    const allCount = (
        statusCounts.Pending === null ||
        statusCounts.Processing === null ||
        statusCounts.Shipped === null ||
        statusCounts["In Transit"] === null ||
        statusCounts["Out for Delivery"] === null ||
        statusCounts.Delivered === null
    )
        ? "..."
        : ` (${(statusCounts.Pending || 0) + (statusCounts.Processing || 0) + (statusCounts.Shipped || 0) + (statusCounts["In Transit"] || 0) + (statusCounts["Out for Delivery"] || 0) + (statusCounts.Delivered || 0)})`;

    const handleConfirmShip = async () => {
        if (!shippingOrder) return setShipError("No order selected");
        if (!estimateDeliveryDate) return setShipError("Please select an estimated delivery date");

        setShipLoading(true);
        setShipError(null);
        try {
            const token = sessionStorage.getItem("accessToken") || localStorage.getItem("accessToken") || "";
            const orderIdentifier = shippingOrder?.raw?.orderId || shippingOrder?.orderId || shippingOrder?.id;
            if (!orderIdentifier) throw new Error("Order identifier missing");

            const url = `${API_BASE_URL}/seller/orders/${encodeURIComponent(orderIdentifier)}/ship`;

            const payload = {
                estimateDeliveryDate: new Date(`${estimateDeliveryDate}T00:00:00Z`).toISOString(),
                deliveryRemark: deliveryRemark || "",
            };

            const res = await fetch(url, {
                method: "PATCH",
                headers: {
                    "Content-Type": "application/json",
                    ...(token ? { token } : {}),
                },
                body: JSON.stringify(payload),
            });

            const json = await res.json();
            if (!res.ok || json?.success === false) {
                throw new Error(json?.message || `Failed to mark as shipped (${res.status})`);
            }

            // Update local orders: mark the matching order as Shipped
            setApiOrders((prev) => {
                const updated = prev.map((o) => {
                    const idMatch = (o.raw?.orderId || o.orderId || o.id) === orderIdentifier;
                    if (idMatch) {
                        return { ...o, status: "Shipped", deliveryDate: new Date(payload.estimateDeliveryDate).toLocaleDateString() };
                    }
                    return o;
                });

                // If we are on Processing tab, remove the shipped order from visible list so it moves to Shipped
                if (activeTab === "Processing") {
                    return updated.filter((o) => (o.raw?.orderId || o.orderId || o.id) !== orderIdentifier);
                }

                return updated;
            });

            // Update counts locally to reflect the change
            setStatusCounts((prev) => {
                const processing = Math.max(0, (prev.Processing || 0) - 1);
                const shipped = (prev.Shipped || 0) + 1;
                return { ...prev, Processing: processing, Shipped: shipped };
            });

            setShipModalOpen(false);
            setShippingOrder(null);
            setEstimateDeliveryDate("");
            setDeliveryRemark("");

            alert(json?.message || "Order marked as shipped");
        } catch (err) {
            console.error("Ship error:", err);
            setShipError(err?.message || "Failed to ship order");
        } finally {
            setShipLoading(false);
        }
    };

    // Client-only move: mark order as Processing and move it to Processing tab
    const handleMarkAsProcessing = (order) => {
        setProcessingError(null);
        const orderIdentifier = order?.raw?.orderId || order?.orderId || order?.id;
        if (!orderIdentifier) return setProcessingError("Order identifier missing");

        setProcessingLoadingId(orderIdentifier);

        const processed = { ...order, status: "Processing" };

        // Remove from current pending apiOrders
        setApiOrders((prev) => prev.filter((o) => (o.raw?.orderId || o.orderId || o.id) !== orderIdentifier));

        // Add to client processing list
        setClientProcessingOrders((prev) => [processed, ...prev]);

        // Update counts
        setStatusCounts((prev) => ({ ...prev, Pending: Math.max(0, (prev.Pending || 0) - 1), Processing: (prev.Processing || 0) + 1 }));

        // done
        setProcessingLoadingId(null);
    };

    // helper: PATCH order status on server
    const patchOrderStatus = async (orderIdentifier, newStatus) => {
        const token = sessionStorage.getItem("accessToken") || localStorage.getItem("accessToken") || "";
        const url = `${API_BASE_URL}/seller/orders/${encodeURIComponent(orderIdentifier)}/status`;
        const res = await fetch(url, {
            method: "PATCH",
            headers: {
                "Content-Type": "application/json",
                ...(token ? { token } : {}),
            },
            body: JSON.stringify({ status: newStatus }),
        });
        const json = await res.json().catch(() => ({}));
        if (!res.ok || json?.success === false) {
            throw new Error(json?.message || `Failed to update status to ${newStatus}`);
        }
        return json;
    };

    // Move a shipped order to In Transit (server + client)
    const moveToInTransit = async (order) => {
        const id = order?.raw?.orderId || order?.orderId || order?.id;
        if (!id) return;
        setMoveError(null);
        setMoveLoadingId(id);

        try {
            await patchOrderStatus(id, "transit");

            // remove from shipped lists
            setApiOrders((prev) => prev.filter((o) => (o.raw?.orderId || o.orderId || o.id) !== id));
            setClientShippedOrders((prev) => prev.filter((o) => (o.raw?.orderId || o.orderId || o.id) !== id));

            const moved = { ...order, status: "In Transit" };
            setClientInTransitOrders((prev) => [moved, ...prev]);

            // update counts
            setStatusCounts((prev) => ({ ...prev, Shipped: Math.max(0, (prev.Shipped || 0) - 1), "In Transit": (prev["In Transit"] || 0) + 1 }));
        } catch (err) {
            console.error("moveToInTransit error:", err);
            setMoveError(err?.message || "Failed to move to In Transit");
            alert(err?.message || "Failed to move to In Transit");
        } finally {
            setMoveLoadingId(null);
        }
    };

    // Move In Transit -> Out for Delivery (server + client)
    const moveToOutForDelivery = async (order) => {
        const id = order?.raw?.orderId || order?.orderId || order?.id;
        if (!id) return;
        setMoveError(null);
        setMoveLoadingId(id);

        try {
            await patchOrderStatus(id, "out_of_delivery");

            setClientInTransitOrders((prev) => prev.filter((o) => (o.raw?.orderId || o.orderId || o.id) !== id));
            const moved = { ...order, status: "Out for Delivery" };
            setClientOutForDeliveryOrders((prev) => [moved, ...prev]);

            setStatusCounts((prev) => ({ ...prev, "In Transit": Math.max(0, (prev["In Transit"] || 0) - 1), "Out for Delivery": (prev["Out for Delivery"] || 0) + 1 }));
        } catch (err) {
            console.error("moveToOutForDelivery error:", err);
            setMoveError(err?.message || "Failed to move to Out for Delivery");
            alert(err?.message || "Failed to move to Out for Delivery");
        } finally {
            setMoveLoadingId(null);
        }
    };

    // Move Out for Delivery -> Delivered (server + client)
    const moveToDelivered = async (order) => {
        const id = order?.raw?.orderId || order?.orderId || order?.id;
        if (!id) return;
        setMoveError(null);
        setMoveLoadingId(id);

        try {
            await patchOrderStatus(id, "delivered");

            setClientOutForDeliveryOrders((prev) => prev.filter((o) => (o.raw?.orderId || o.orderId || o.id) !== id));
            const moved = { ...order, status: "Delivered" };
            setClientDeliveredOrders((prev) => [moved, ...prev]);

            setStatusCounts((prev) => ({ ...prev, "Out for Delivery": Math.max(0, (prev["Out for Delivery"] || 0) - 1), Delivered: (prev.Delivered || 0) + 1 }));
        } catch (err) {
            console.error("moveToDelivered error:", err);
            setMoveError(err?.message || "Failed to move to Delivered");
            alert(err?.message || "Failed to move to Delivered");
        } finally {
            setMoveLoadingId(null);
        }
    };

    return (
        <div className="dashboard w-full min-h-screen flex">
            <Leftsidebar />

            <main className="flex-1 p-6 pl-8">
                <LeftResponsivesidebar />
                <Headmenu />

                <main className="flex">
                    <div className="w-full">
                        <div className="flex flex-col sm:flex-row space-y-5 sm:space-y-0 justify-between sm:items-center py-2">
                            <div>
                                <p className="text-xl">Order Management</p>
                            </div>
                        </div>

                                <div className="flex gap-4 px-0 py-4 bg-white overflow-x-auto">
                            {["All", "Pending", "Processing", "Shipped", "In Transit", "Out for Delivery", "Delivered"].map((tab) => (
                                <button
                                    key={tab}
                                    onClick={() => {
                                        if (tab === activeTab) return;
                                        setLoadingApi(true);
                                        setApiError(null);
                                        setActiveTab(tab);
                                    }}
                                    className={`text-sm whitespace-nowrap pb-1 ${activeTab === tab
                                        ? "font-semibold border-b-2 border-gray-900 text-[#095228]"
                                        : "text-[#929292]"
                                        }`}
                                >
                                    {tab}
                                    {tab === "All" && allCount}
                                    {tab === "Pending" && renderCount("Pending")}
                                    {tab === "Processing" && renderCount("Processing")}
                                    {tab === "Shipped" && renderCount("Shipped")}
                                    {tab === "In Transit" && renderCount("In Transit")}
                                    {tab === "Out for Delivery" && renderCount("Out for Delivery")}
                                    {tab === "Delivered" && renderCount("Delivered")}
                                </button>
                            ))}
                        </div>

                        <div className="px-0 py-4 space-y-4">
                            {loadingApi && (
                                <div className="text-sm text-gray-500">Loading...</div>
                            )}
                            {!loadingApi && apiError && (
                                <div className="text-sm text-red-600">Error: {apiError}</div>
                            )}

                            {!loadingApi && !apiError && displayedOrders.length === 0 && (
                                <div className="py-10 text-center text-gray-500" role="status">
                                    No orders found.
                                </div>
                            )}

                            {!loadingApi && !apiError && displayedOrders.map((order, index) => (
                                <div
                                    key={order.id}
                                    className="bg-white rounded-2xl p-2 md:p-4 shadow-sm border border-gray-200"
                                >
                                    <div className="flex justify-between items-start md:items-center mb-0">
                                        <div>
                                            <span className="text-xs text-[#98911F] font-semibold">
                                                Order ID: {order.orderId}
                                            </span>
                                        </div>

                                        <span className="px-2 md:px-3 py-1 bg-[#FF7A51] text-[#FFFFFF] text-[10px] md:text-xs rounded-full">
                                            {order.status}
                                        </span>
                                    </div>

                                    <div className="flex justify-between items-start md:items-center mb-3">
                                        <div>
                                            <div className="flex items-center gap-1 md:gap-1 mt-1">
                                                <div className="text-xs text-[#667085]">
                                                    <span className="text-[10px] md:text-xs">Order date:</span> <span className="font-semibold text-[#1D2939] text-[10px] md:text-xs">{order.date}</span>
                                                </div>
                                                <span className="text-[#D0D5DD]">|</span>
                                                <span className="text-[10px] md:text-xs text-[#12B76A] flex items-start md:items-center gap-1">
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none" className="">
                                                        <path d="M6.5 7.37524H1C0.795 7.37524 0.625 7.20524 0.625 7.00024V3.81024C0.625 3.65024 0.724995 3.51023 0.869995 3.45523C1.02 3.40023 1.185 3.44525 1.285 3.56525C1.59 3.93025 2.065 4.13524 2.535 4.12024C2.935 4.11024 3.30499 3.96025 3.58499 3.69525C3.71499 3.58525 3.81999 3.45524 3.89999 3.31024C4.05499 3.04524 4.13 2.75524 4.125 2.46024C4.115 2.00024 3.91501 1.58024 3.57501 1.27524C3.45501 1.17024 3.415 1.00523 3.47 0.860229C3.525 0.715229 3.66501 0.615234 3.82001 0.615234H7.5C7.705 0.615234 7.875 0.785234 7.875 0.990234V5.99023C7.875 6.76023 7.26 7.37524 6.5 7.37524ZM1.375 6.62524H6.5C6.845 6.62524 7.125 6.34524 7.125 6.00024V1.37524H4.595C4.77 1.70024 4.865 2.06525 4.875 2.44525C4.885 2.88025 4.77499 3.31024 4.55499 3.68524C4.43499 3.90024 4.27 4.10525 4.09 4.25525C3.69 4.63525 3.13999 4.86024 2.55499 4.87524C2.13499 4.89024 1.73 4.78525 1.38 4.59525V6.62524H1.375Z" fill="#12B76A" />
                                                        <path d="M9.5 10.375H9C8.795 10.375 8.625 10.205 8.625 10C8.625 9.655 8.345 9.375 8 9.375C7.655 9.375 7.375 9.655 7.375 10C7.375 10.205 7.205 10.375 7 10.375H5C4.795 10.375 4.625 10.205 4.625 10C4.625 9.655 4.345 9.375 4 9.375C3.655 9.375 3.375 9.655 3.375 10C3.375 10.205 3.205 10.375 3 10.375H2.5C1.465 10.375 0.625 9.535 0.625 8.5V7C0.625 6.795 0.795 6.625 1 6.625H6.5C6.845 6.625 7.125 6.345 7.125 6V2.5C7.125 2.295 7.295 2.125 7.5 2.125H8.42001C8.91501 2.125 9.36999 2.39001 9.61499 2.82001L10.47 4.315C10.535 4.43 10.535 4.575 10.47 4.69C10.405 4.805 10.28 4.875 10.145 4.875H9.5C9.43 4.875 9.375 4.93 9.375 5V6.5C9.375 6.57 9.43 6.625 9.5 6.625H11C11.205 6.625 11.375 6.795 11.375 7V8.5C11.375 9.535 10.535 10.375 9.5 10.375ZM9.32501 9.625H9.5C10.12 9.625 10.625 9.12 10.625 8.5V7.375H9.5C9.02 7.375 8.625 6.98 8.625 6.5V5C8.625 4.52 9.015 4.125 9.5 4.125L8.965 3.19C8.855 2.995 8.64501 2.875 8.42001 2.875H7.875V6C7.875 6.76 7.26 7.375 6.5 7.375H1.375V8.5C1.375 9.12 1.88 9.625 2.5 9.625H2.67499C2.83999 9.05 3.37 8.625 4 8.625C4.63 8.625 5.16001 9.05 5.32501 9.625H6.67999C6.84499 9.05 7.375 8.625 8.005 8.625C8.635 8.625 9.16001 9.05 9.32501 9.625Z" fill="#12B76A" />
                                                    </svg>Estimated delivery: {order.deliveryDate || "N/A"}
                                                </span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="flex gap-4">
                                        <div className="relative flex-shrink-0">
                                            <img
                                                src={order.image || "https://csspicker.dev/api/image/?q=adidas+running+shoes&image_type=photo"}
                                                alt="Product"
                                                className="w-20 h-20 object-cover rounded-lg bg-gray-100"
                                            />
                                            <span className="absolute top-0 left-0 bg-red-600 text-white text-[7px] px-2 py-0.5 rounded-tl-lg rounded-br-lg font-semibold">
                                                {order.discount}
                                            </span>
                                        </div>

                                        <div className="flex-1">
                                            <h3 className="text-sm text-[#232321] font-semibold mb-1">{order.productName}</h3>
                                            <p className="text-xs text-[#095228]  font-semibold mb-2">
                                                {order.color} | {order.size}
                                            </p>

                                            <div className="flex justify-between items-center">
                                                <div className="flex items-center gap-2">
                                                    <span className="text-lg font-bold">₹{order.price}</span>
                                                    {order.paid && (
                                                        <span className="text-xs text-green-600 font-semibold">
                                                            Paid
                                                        </span>
                                                    )}
                                                </div>
                                                <span className="text-sm text-[#5C5F6A]">
                                                    Qty: {order.quantity}
                                                </span>
                                            </div>
                                        </div>
                                    </div>

                                                        {/* Action buttons per tab */}
                                    {activeTab === "Processing" && (
                                        <button
                                            onClick={() => {
                                                setShippingOrder(order);
                                                setEstimateDeliveryDate("");
                                                setDeliveryRemark("");
                                                setShipError(null);
                                                setShipModalOpen(true);
                                            }}
                                            className="w-full mt-4 bg-[#095228] text-[#FFFFFF] py-3 rounded-full font-semibold text-sm hover:bg-green-800"
                                        >
                                            Mark as Shipped
                                        </button>
                                    )}

                                    {activeTab === "Shipped" && (order.orderId || order.raw) && (
                                        <button
                                            onClick={() => moveToInTransit(order)}
                                            disabled={moveLoadingId === (order.raw?.orderId || order.orderId || order.id)}
                                            className="w-full mt-4 bg-[#0B5E48] text-[#FFFFFF] py-3 rounded-full font-semibold text-sm hover:bg-green-800 disabled:opacity-60"
                                        >
                                            {moveLoadingId === (order.raw?.orderId || order.orderId || order.id) ? "Marking..." : "Mark as Transit"}
                                        </button>
                                    )}

                                    {activeTab === "In Transit" && (order.orderId || order.raw) && (
                                        <button
                                            onClick={() => moveToOutForDelivery(order)}
                                            disabled={moveLoadingId === (order.raw?.orderId || order.orderId || order.id)}
                                            className="w-full mt-4 bg-[#0B5E48] text-[#FFFFFF] py-3 rounded-full font-semibold text-sm hover:bg-green-800 disabled:opacity-60"
                                        >
                                            {moveLoadingId === (order.raw?.orderId || order.orderId || order.id) ? "Marking..." : "Mark as Out for Delivery"}
                                        </button>
                                    )}

                                    {activeTab === "Out for Delivery" && (order.orderId || order.raw) && (
                                        <button
                                            onClick={() => moveToDelivered(order)}
                                            disabled={moveLoadingId === (order.raw?.orderId || order.orderId || order.id)}
                                            className="w-full mt-4 bg-[#0B5E48] text-[#FFFFFF] py-3 rounded-full font-semibold text-sm hover:bg-green-800 disabled:opacity-60"
                                        >
                                            {moveLoadingId === (order.raw?.orderId || order.orderId || order.id) ? "Marking..." : "Mark as Delivered"}
                                        </button>
                                    )}

                                    {activeTab === "Pending" && (order.orderId || order.raw) && (
                                        <button
                                            onClick={() => handleMarkAsProcessing(order)}
                                            disabled={processingLoadingId === (order.raw?.orderId || order.orderId || order.id)}
                                            className="w-full mt-4 bg-[#095228] text-[#FFFFFF] py-3 rounded-full font-semibold text-sm hover:bg-green-800 disabled:opacity-60"
                                        >
                                            {processingLoadingId === (order.raw?.orderId || order.orderId || order.id) ? "Processing..." : "Mark as Processing"}
                                        </button>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                </main>
            </main>

            {/* Ship modal (global inside panel) */}
            {shipModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
                    <div className="bg-white rounded p-6 w-full max-w-md">
                        <h3 className="text-lg font-semibold mb-3">Mark Order as Shipped</h3>

                        <div className="space-y-3">
                            <div>
                                <label className="block text-sm mb-1">Estimated delivery date</label>
                                <input
                                    type="date"
                                    value={estimateDeliveryDate}
                                    onChange={(e) => setEstimateDeliveryDate(e.target.value)}
                                    className="w-full border rounded px-3 py-2"
                                />
                            </div>

                            <div>
                                <label className="block text-sm mb-1">Delivery remark</label>
                                <textarea
                                    value={deliveryRemark}
                                    onChange={(e) => setDeliveryRemark(e.target.value)}
                                    className="w-full border rounded px-3 py-2"
                                    rows={3}
                                />
                            </div>

                            {shipError && <div className="text-sm text-red-600">{shipError}</div>}

                            <div className="flex justify-end gap-2 mt-4">
                                <button
                                    onClick={handleConfirmShip}
                                    disabled={shipLoading}
                                    className="bg-[#095228] text-white px-4 py-2 rounded"
                                >
                                    {shipLoading ? "Processing..." : "Confirm"}
                                </button>

                                <button onClick={() => setShipModalOpen(false)} className="bg-gray-200 text-gray-800 px-4 py-2 rounded">
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default OrderManagement;